/**
 * Nuxt plugin for Datadog Real User Monitoring (RUM) and Logging integration.
 *
 * This plugin:
 * 1. Skips initialization for federated and development environments
 * 2. Detects bot traffic and adjusts sampling rate
 * 3. Initializes Datadog RUM and Logging with custom configuration
 *
 * @type {import('nuxt/app').NuxtPlugin}
 * @name datadog
 * @see https://docs.datadoghq.com/getting_started/site/
 */
export default defineNuxtPlugin({
  name: 'datadog',
  parallel: true,
  async setup() {
    // Skip initialization for federated and development environments
    if (useRuntimeConfig().public.isFederated) return
    if (import.meta.dev) return

    const { datadogRum } = await import('@datadog/browser-rum')
    const { datadogLogs } = await import('@datadog/browser-logs')

    /**
     * Comprehensive bot detection pattern.
     * Matches various web crawlers and bot user agents.
     *
     * @type {string}
     */
    const botPattern =
      '(googlebot/|bot|Googlebot-Mobile|Googlebot-Image|Google favicon|Mediapartners-Google|bingbot|slurp|java|wget|curl|Commons-HttpClient|Python-urllib|libwww|httpunit|nutch|phpcrawl|msnbot|jyxobot|FAST-WebCrawler|FAST Enterprise Crawler|biglotron|teoma|convera|seekbot|gigablast|exabot|ngbot|ia_archiver|GingerCrawler|webmon |httrack|webcrawler|grub.org|UsineNouvelleCrawler|antibot|netresearchserver|speedy|fluffy|bibnum.bnf|findlink|msrbot|panscient|yacybot|AISearchBot|IOI|ips-agent|tagoobot|MJ12bot|dotbot|woriobot|yanga|buzzbot|mlbot|yandexbot|purebot|Linguee Bot|Voyager|CyberPatrol|voilabot|baiduspider|citeseerxbot|spbot|twengabot|postrank|turnitinbot|scribdbot|page2rss|sitebot|linkdex|Adidxbot|blekkobot|ezooms|dotbot|Mail.RU_Bot|discobot|heritrix|findthatfile|europarchive.org|NerdByNature.Bot|sistrix crawler|ahrefsbot|Aboundex|domaincrawler|wbsearchbot|summify|ccbot|edisterbot|seznambot|ec2linkfinder|gslfbot|aihitbot|intelium_bot|facebookexternalhit|yeti|RetrevoPageAnalyzer|lb-spider|sogou|lssbot|careerbot|wotbox|wocbot|ichiro|DuckDuckBot|lssrocketcrawler|drupact|webcompanycrawler|acoonbot|openindexspider|gnam gnam spider|web-archive-net.com.bot|backlinkcrawler|coccoc|integromedb|content crawler spider|toplistbot|seokicks-robot|it2media-domain-crawler|ip-web-crawler.com|siteexplorer.info|elisabot|proximic|changedetection|blexbot|arabot|WeSEE:Search|niki-bot|CrystalSemanticsBot|rogerbot|360Spider|psbot|InterfaxScanBot|Lipperhey SEO Service|CC Metadata Scaper|g00g1e.net|GrapeshotCrawler|urlappendbot|brainobot|fr-crawler|binlar|SimpleCrawler|Livelapbot|Twitterbot|cXensebot|smtbot|bnf.fr_bot|A6-Indexer|ADmantX|Facebot|Twitterbot|OrangeBot|memorybot|AdvBot|MegaIndex|SemanticScholarBot|ltx71|nerdybot|xovibot|BUbiNG|Qwantify|archive.org_bot|Applebot|TweetmemeBot|crawler4j|findxbot|SemrushBot|yoozBot|lipperhey|y!j-asr|Domain Re-Animator Bot|AddThis)'

    /**
     * Regular expression to test for bot user agents.
     *
     * @type {RegExp}
     */
    const botRegex = new RegExp(botPattern, 'i')

    /**
     * Determine sampling rate based on bot detection.
     * Bots get 0% sampling, while real users get 10% sampling.
     *
     * @type {number}
     */
    const sampleRate = botRegex.test(navigator.userAgent) ? 0 : 100

    /**
     * Runtime configuration for Datadog settings.
     * @type {Object}
     */
    const config = useRuntimeConfig()

    /**
     * Initialize Datadog Real User Monitoring (RUM).
     * Configures session tracking, replay, and privacy settings.
     *
     * @see https://docs.datadoghq.com/real_user_monitoring/
     */
    datadogRum.init({
      applicationId: config.public.datadog.ddApplicationId,
      clientToken: config.public.datadog.ddClientToken,
      // `site` refers to the Datadog site parameter of your organization
      // see https://docs.datadoghq.com/getting_started/site/
      site: 'datadoghq.eu',
      service: config.public.datadog.ddService,
      env: config.public.datadog.ddEnv,
      version: config.public.app_version as string,
      sessionSampleRate: sampleRate,
      sessionReplaySampleRate: 100,
      startSessionReplayRecordingManually: false,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
    })
    datadogRum.startSessionReplayRecording()

    /**
     * Initialize Datadog Logs.
     * Configures log forwarding and session tracking.
     *
     * @see https://docs.datadoghq.com/logs/
     */
    datadogLogs.init({
      clientToken: config.public.datadog.ddClientToken,
      site: 'datadoghq.eu',
      forwardErrorsToLogs: true,
      service: config.public.datadog.ddService,
      sessionSampleRate: 100,
      version: config.public.app_version as string,
      env: config.public.datadog.ddEnv,
    })
  },
})
